import { useQuery } from '@tanstack/react-query';
import { PART_LOOKUP_ROW_LIMIT } from '@avisoma/shared';
import { Badge } from '../../catalyst/badge';
import { CollapsibleCard } from '../../components/CollapsibleCard';
import { formatShortId } from '../../components/ID';
import { Link } from '../../components/Link';
import backend from '../../services/backend';
import { engineLabel, otherProjects, projectGraphHref } from './partElsewhere';

type Props = {
  /** The selected part node id (`part:<P/N>|<S/N>`, the S/N without whitespace): the index key. */
  partKey: string;
  /** The project whose graph is open: left out of the list. */
  projectId: string;
};

const count = (n: number, one: string, many: string) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

/**
 * The other projects whose documents name the selected part (DR-3662 cross-project part index), each document
 * with the engine it puts the part on. Fetched only when a part is selected.
 */
export function PartElsewhere({ partKey, projectId }: Props) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['getPartRecords', partKey, projectId],
    queryFn: () => backend.getPartRecords(partKey).then((res) => res.data),
    enabled: partKey.startsWith('part:'),
    staleTime: 60_000,
  });
  const projects = data ? otherProjects(data, projectId) : [];
  const title =
    data && projects.length > 0
      ? `Also seen in ${count(projects.length, 'other project', 'other projects')}`
      : 'Other projects';

  return (
    <CollapsibleCard storageKey="projectGraph.partElsewhere" title={title} defaultExpanded>
      <div className="flex flex-col gap-3 py-1 text-sm text-zinc-900 dark:text-zinc-100">
        {isLoading && <p className="text-zinc-500 dark:text-zinc-400">Looking up other projects…</p>}
        {isError && <p className="text-red-600 dark:text-red-400">Could not load the part index.</p>}
        {data && projects.length === 0 && (
          <p className="text-zinc-500 dark:text-zinc-400">No other project names this part.</p>
        )}
        {projects.map((project) => (
          <section key={project.projectId} className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <Link to={projectGraphHref(project.projectId, partKey)} className="font-medium">
                Project {formatShortId(project.projectId)}
              </Link>
              {project.workingEsn && <span className="text-zinc-600 dark:text-zinc-300">ESN {project.workingEsn}</span>}
              {project.organisationName && (
                <span className="text-zinc-500 dark:text-zinc-400">{project.organisationName}</span>
              )}
              <Badge color="zinc">{count(project.entities.length, 'document', 'documents')}</Badge>
            </div>
            <ul className="ml-1 flex flex-col gap-1 border-l-2 border-zinc-200 pl-3 text-xs dark:border-zinc-700">
              {project.entities.map((entity) => (
                <li key={entity.reconstructedDocumentId} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <Link to={`/reconstructed-documents/${encodeURIComponent(entity.reconstructedDocumentId)}`}>
                    {entity.category}
                  </Link>
                  <span className="text-zinc-500 dark:text-zinc-400">{entity.documentDate ?? 'undated'}</span>
                  {entity.engines.map((engine) => (
                    <Badge
                      key={`${engine.engineRole}:${engine.engineKey}`}
                      color={engine.engineRole === 'removed-from' ? 'amber' : 'zinc'}
                    >
                      {engineLabel(engine)}
                    </Badge>
                  ))}
                </li>
              ))}
            </ul>
          </section>
        ))}
        {data?.truncated && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            Showing the newest {PART_LOOKUP_ROW_LIMIT.toLocaleString('en-US')} records only.
          </p>
        )}
      </div>
    </CollapsibleCard>
  );
}
